import React from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Users, 
  Target, 
  TrendingUp, 
  BarChart3,
  MapPin,
  Info,
  CheckCircle
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';

interface CDModalProps {
  data: {
    active_cds: number;
    cd_analytics: {
      cds_by_status: Array<{
        status: string;
        count: number;
        percentage: number;
      }>;
      cds_by_region: Array<{
        region: string;
        count: number;
        capacity: number;
        utilization: number;
      }>;
      cd_performance: Array<{
        cd: string;
        throughput: number;
        efficiency: number;
        cost_per_unit: number;
      }>;
    };
  };
}

export const CDModal: React.FC<CDModalProps> = ({ data }) => {
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  return (
    <div className="space-y-6">
      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {data.cd_analytics.cds_by_status.map((status, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.1 }}
            className="rounded-lg p-4 shadow border"
            style={{
              backgroundColor: 'var(--bg-card)',
              borderColor: 'var(--border-primary)'
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium style={{ color: 'var(--text-secondary)' }}">{status.status}</p>
                <p className="text-2xl font-bold style={{ color: 'var(--text-primary)' }}">{status.count}</p>
                <p className="text-xs style={{ color: 'var(--text-tertiary)' }}">{status.percentage.toFixed(1)}%</p>
              </div>
              <div 
                className="h-8 w-8 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  opacity: 0.8
                }}
              >
                <Building2 
                  className="h-4 w-4" 
                  style={{ color: 'var(--primary)' }}
                />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Regional Distribution */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="rounded-xl p-6 shadow border"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-primary)'
        }}
      >
        <h3 
          className="text-xl font-semibold mb-6 flex items-center"
          style={{ color: 'var(--text-primary)' }}
        >
          <MapPin 
            className="h-5 w-5 mr-2" 
            style={{ color: 'var(--primary)' }}
          />
          Distribuição Regional
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.cd_analytics.cds_by_region}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="region" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'var(--bg-card)',
                border: '1px solid var(--border-primary)',
                borderRadius: '8px'
              }}
            />
            <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Performance */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="rounded-xl p-6 shadow border"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-primary)'
        }}
      >
        <h3 
          className="text-xl font-semibold mb-6 flex items-center"
          style={{ color: 'var(--text-primary)' }}
        >
          <BarChart3 
            className="h-5 w-5 mr-2" 
            style={{ color: 'var(--primary)' }}
          />
          Performance dos CDs
        </h3>
        <div className="space-y-4">
          {data.cd_analytics.cd_performance.map((cd, index) => (
            <div 
              key={index} 
              className="flex items-center justify-between p-4 rounded-lg"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                opacity: 0.6
              }}
            >
              <div>
                <h4 
                  className="font-semibold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {cd.cd}
                </h4>
                <p 
                  className="text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Throughput: {formatNumber(cd.throughput)} unidades
                </p>
              </div>
              <div className="text-right">
                <div 
                  className="text-lg font-bold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {cd.efficiency.toFixed(1)}%
                </div>
                <div 
                  className="text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  R$ {cd.cost_per_unit.toFixed(2)}/unidade
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Data Source Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="p-4 rounded-lg border relative overflow-hidden"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-primary)',
          boxShadow: 'var(--shadow-md)'
        }}
      >
        <div className="relative">
          <div className="flex items-start space-x-3">
            <Info 
              className="h-5 w-5 mt-0.5 flex-shrink-0" 
              style={{ color: 'var(--primary)' }}
            />
            <div>
              <h4 
                className="font-semibold mb-1"
                style={{ color: 'var(--text-primary)' }}
              >
                Origem dos Dados
              </h4>
              <p 
                className="text-sm"
                style={{ color: 'var(--text-secondary)' }}
              >
                Os dados dos Centros de Distribuição são coletados automaticamente dos sistemas de WMS (Warehouse Management System), 
                incluindo informações sobre capacidade, utilização, throughput e custos operacionais.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

interface ClientModalProps {
  data: {
    total_clients: number;
    client_analytics: {
      portfolio_health?: any;
      segmentation?: any;
      risk_analysis?: any;
      geographic_distribution?: any;
      client_segments?: Array<{
        segment: string;
        count: number;
        revenue: number;
        percentage: number;
      }>;
      client_satisfaction?: {
        very_satisfied: number;
        satisfied: number;
        neutral: number;
        dissatisfied: number;
        very_dissatisfied: number;
        nps_score: number;
      };
      client_growth?: Array<{
        month: string;
        new_clients: number;
        churned_clients: number;
        net_growth: number;
      }>;
    };
  };
}

export const ClientModal: React.FC<ClientModalProps> = ({ data }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Segments */}
      {data.client_analytics.client_segments && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {data.client_analytics.client_segments?.map((segment, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.1 }}
            className="rounded-lg p-4 shadow border"
            style={{
              backgroundColor: 'var(--bg-card)',
              borderColor: 'var(--border-primary)'
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium style={{ color: 'var(--text-secondary)' }}">{segment.segment}</p>
                <p className="text-2xl font-bold style={{ color: 'var(--text-primary)' }}">{segment.count}</p>
                <p className="text-xs style={{ color: 'var(--text-tertiary)' }}">{segment.percentage}%</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold style={{ color: 'var(--text-primary)' }}">
                  {formatCurrency(segment.revenue)}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      )}

      {/* Satisfaction */}
      {data.client_analytics.client_satisfaction && (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="rounded-xl p-6 shadow border"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-primary)'
        }}
      >
        <h3 
          className="text-xl font-semibold mb-6 flex items-center"
          style={{ color: 'var(--text-primary)' }}
        >
          <Users 
            className="h-5 w-5 mr-2" 
            style={{ color: 'var(--primary)' }}
          />
          Satisfação dos Clientes
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center">
            <div 
              className="text-3xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              {data.client_analytics.client_satisfaction?.very_satisfied}%
            </div>
            <div 
              className="text-sm"
              style={{ color: 'var(--text-secondary)' }}
            >
              Muito Satisfeitos
            </div>
          </div>
          <div className="text-center">
            <div 
              className="text-3xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              {data.client_analytics.client_satisfaction?.satisfied}%
            </div>
            <div 
              className="text-sm"
              style={{ color: 'var(--text-secondary)' }}
            >
              Satisfeitos
            </div>
          </div>
          <div className="text-center">
            <div 
              className="text-3xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              {data.client_analytics.client_satisfaction?.neutral}%
            </div>
            <div 
              className="text-sm"
              style={{ color: 'var(--text-secondary)' }}
            >
              Neutros
            </div>
          </div>
          <div className="text-center">
            <div 
              className="text-3xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              {data.client_analytics.client_satisfaction?.dissatisfied}%
            </div>
            <div 
              className="text-sm"
              style={{ color: 'var(--text-secondary)' }}
            >
              Insatisfeitos
            </div>
          </div>
          <div className="text-center">
            <div 
              className="text-3xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              {data.client_analytics.client_satisfaction?.very_dissatisfied}%
            </div>
            <div 
              className="text-sm"
              style={{ color: 'var(--text-secondary)' }}
            >
              Muito Insatisfeitos
            </div>
          </div>
        </div>
        <div className="mt-6 text-center">
          <div
            className="text-2xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            NPS Score: {data.client_analytics.client_satisfaction?.nps_score}
          </div>
        </div>
      </motion.div>
      )}

      {/* Data Source Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="p-4 rounded-lg border shadow-md"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-primary)'
        }}
      >
        <div className="flex items-start space-x-3">
          <Info 
            className="h-5 w-5 mt-0.5 flex-shrink-0" 
            style={{ color: 'var(--primary)' }}
          />
          <div>
            <h4 
              className="font-semibold mb-1"
              style={{ color: 'var(--text-primary)' }}
            >
              Origem dos Dados
            </h4>
            <p 
              className="text-sm"
              style={{ color: 'var(--text-secondary)' }}
            >
              Os dados de clientes são coletados do CRM e sistemas de atendimento, incluindo informações sobre segmentação, 
              satisfação (via pesquisas NPS) e crescimento da base de clientes.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

interface EfficiencyModalProps {
  data: {
    efficiency_score: number;
    performance_metrics: {
      on_time_delivery: number;
      cost_efficiency: number;
      customer_satisfaction: number;
      operational_efficiency: number;
    };
    strategic_insights: Array<{
      insight: string;
      impact: string;
      recommendation: string;
    }>;
  };
}

export const EfficiencyModal: React.FC<EfficiencyModalProps> = ({ data }) => {
  // Função para tratar valores undefined
  const safeValue = (value: number | undefined, defaultValue: number = 0) => {
    return value !== undefined ? value : defaultValue
  }

  return (
    <div className="space-y-6">
      {/* Performance Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-lg p-4 shadow border"
            style={{
              backgroundColor: 'var(--bg-card)',
              borderColor: 'var(--border-primary)'
            }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-secondary)' }}
              >
                Eficiência Geral
              </p>
              <p 
                className="text-2xl font-bold"
                style={{ color: 'var(--text-primary)' }}
              >
                {safeValue(data.efficiency_score).toFixed(1)}%
              </p>
            </div>
            <Target 
              className="h-8 w-8" 
              style={{ color: 'var(--primary)' }}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-lg p-4 shadow border"
            style={{
              backgroundColor: 'var(--bg-card)',
              borderColor: 'var(--border-primary)'
            }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-secondary)' }}
              >
                Entrega Pontual
              </p>
              <p 
                className="text-2xl font-bold"
                style={{ color: 'var(--text-primary)' }}
              >
                {safeValue(data.performance_metrics?.on_time_delivery).toFixed(1)}%
              </p>
            </div>
            <CheckCircle 
              className="h-8 w-8" 
              style={{ color: 'var(--primary)' }}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="rounded-lg p-4 shadow border"
            style={{
              backgroundColor: 'var(--bg-card)',
              borderColor: 'var(--border-primary)'
            }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-secondary)' }}
              >
                Eficiência de Custo
              </p>
              <p 
                className="text-2xl font-bold"
                style={{ color: 'var(--text-primary)' }}
              >
                {safeValue(data.performance_metrics?.cost_efficiency).toFixed(1)}%
              </p>
            </div>
            <TrendingUp 
              className="h-8 w-8" 
              style={{ color: 'var(--primary)' }}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="rounded-lg p-4 shadow border"
            style={{
              backgroundColor: 'var(--bg-card)',
              borderColor: 'var(--border-primary)'
            }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-secondary)' }}
              >
                Satisfação
              </p>
              <p 
                className="text-2xl font-bold"
                style={{ color: 'var(--text-primary)' }}
              >
                {safeValue(data.performance_metrics?.customer_satisfaction).toFixed(1)}%
              </p>
            </div>
            <Users 
              className="h-8 w-8" 
              style={{ color: 'var(--primary)' }}
            />
          </div>
        </motion.div>
      </div>

      {/* Strategic Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="rounded-xl p-6 shadow border"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-primary)'
        }}
      >
        <h3 
          className="text-xl font-semibold mb-6 flex items-center"
          style={{ color: 'var(--text-primary)' }}
        >
          <Target 
            className="h-5 w-5 mr-2" 
            style={{ color: 'var(--primary)' }}
          />
          Insights Estratégicos
        </h3>
        <div className="space-y-4">
          {(data.strategic_insights || []).map((insight, index) => (
            <div 
              key={index} 
              className="p-4 rounded-lg"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                opacity: 0.6
              }}
            >
              <h4 
                className="font-semibold mb-2"
                style={{ color: 'var(--text-primary)' }}
              >
                {insight.insight}
              </h4>
              <p 
                className="text-sm mb-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                {insight.impact}
              </p>
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-secondary)' }}
              >
                {insight.recommendation}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Data Source Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="p-4 rounded-lg border relative overflow-hidden"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-primary)',
          boxShadow: 'var(--shadow-md)'
        }}
      >
        <div className="relative">
          <div className="flex items-start space-x-3">
            <Info 
              className="h-5 w-5 mt-0.5 flex-shrink-0" 
              style={{ color: 'var(--primary)' }}
            />
            <div>
              <h4 
                className="font-semibold mb-1"
                style={{ color: 'var(--text-primary)' }}
              >
                Origem dos Dados
              </h4>
              <p 
                className="text-sm"
                style={{ color: 'var(--text-secondary)' }}
              >
                Os dados de eficiência são calculados a partir de múltiplas fontes: sistemas de transporte, 
                CRM, pesquisas de satisfação e métricas operacionais, consolidados em tempo real.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
